import React, { useEffect, useState } from 'react';
import { ENavType } from 'constants/nav';
import NavStep from 'components/NavStep/NavStep';
import { Form } from 'antd';
import ButtonFooter from 'components/ButtonFooter/ButtonFooter';
import { TAnswerTrainingResults, TTrainingResult } from 'types/trainingResult';
import { TQuestions } from 'types/questions';
import { useHistory, useParams } from 'react-router-dom';
import commonRender from 'hook/commonRender';
import { convertData, converDataAfterSave } from 'libs/helpers/convertData';
import EditTraining from 'libs/hook/EditTraining';
import { IndexedObject } from 'constants/types';
import { routers } from 'constants/router';
import ModalConfirm from 'components/ModalConfirm/ModalConfirm';
import HeaderTraining from 'components/HeaderTraining';
import AssignmentQuestionForm from './AssignmentQuestionForm3';
import { configTable1 } from './AssignmentQuestion3.state';
import styles from './style.module.scss';

const AssignmentQuestion3: React.FC<{
  trainingResult: TTrainingResult;
  updateTrainingResult: (data: TAnswerTrainingResults, id: string) => void;
  resetTrainingResult: () => void;
}> = ({ trainingResult, updateTrainingResult, resetTrainingResult }) => {
  const [nextStep, setNextStep] = useState(false);
  const [urlLink, setUrlLink] = useState<string | undefined>();
  const [configTable, setConfigTable] = useState<any>();
  const [trainingResultState, setTrainingResultState] = useState<TTrainingResult | null>();
  const [data, setData] = useState<TQuestions[]>([]);
  const [failedData, setFailedData] = useState<IndexedObject>({});
  const [showModal, setShowModal] = useState(false);
  const { trainingId } = useParams<{ trainingId: string }>();
  const [renderValue] = commonRender();
  const currentStep = 3;

  const [form] = Form.useForm();
  const history = useHistory();

  EditTraining();

  useEffect(() => {
    if (trainingResultState?.updated) {
      if (urlLink) {
        history.push(urlLink);
      } else if (nextStep) {
        history.push(routers.U7_4.path.replace(':trainingId', trainingId));
      } else {
        history.push(routers.U7_2.path.replace(':trainingId', trainingId));
      }
      resetTrainingResult();
    }
  }, [trainingResultState]);

  useEffect(() => {
    setTrainingResultState(trainingResult);
    const defaultResult = createDefaultStep3Result();
    const result = trainingResult?.step3?.isCompleted === true ? trainingResult : defaultResult;
    setConfigTable(configTable1(trainingResult.training?.template ?? {}, result));
  }, [trainingResult]);

  // 回答結果の初期状態を作成
  const createDefaultStep3Result = () => {
    const answer = getDefaultRadioAnswers(trainingResult, currentStep);
    if (answer) {
      return { ...trainingResult, step3: { isCompleted: false, answer } };
    }
    return {};
  };

  // 指定ステップの質問を取得
  const getQuestionsForStep = (trainingResult: TTrainingResult, currentStep: number) =>
    trainingResult?.training?.template?.step2
      .filter((assignment) => assignment.step === currentStep)
      .flatMap((item) => item.questions);

  // radioタイプ質問のデフォルト回答取得
  const getDefaultRadioAnswers = (trainingResult: TTrainingResult, currentStep: number) =>
    getRadioAnswers(getQuestionsForStep(trainingResult, currentStep));

  // radioタイプ質問の回答データ整形
  const getRadioAnswers = (questions: any) =>
    questions
      ?.filter((item: any) => item.fieldType === 'radio')
      .map((item: any) => ({
        questionId: item.questionId,
        fieldCd: item.fieldCd,
        answerValue: item.answer[0]?.value,
      }));

  useEffect(() => {
    if (trainingResult?.training?.template?.step2) {
      const step = trainingResult?.training?.template?.step2.filter((assignment) => assignment.step === currentStep);
      setData(step);
      if (trainingResult?.step3?.answer) {
        form.setFieldsValue(convertData(trainingResult?.step3?.answer));
      }
    }
  }, [trainingId, trainingResult]);

  const onFinish = (values: IndexedObject) => {
    const answer = converDataAfterSave(data, values);
    updateTrainingResult({ isCompleted: true, answer }, trainingId);
  };

  const onFinishFailed = (errorInfo: IndexedObject) => {
    setShowModal(true);
    setFailedData(errorInfo.values);
  };

  const onClickPreStep = () => {
    form.submit();
  };

  const onClickNextStep = () => {
    setNextStep(true);
    form.submit();
  };

  const handelConfirm = () => {
    const answer = converDataAfterSave(data, failedData);
    updateTrainingResult({ isCompleted: false, answer }, trainingId);
  };

  const onChangeValue = (changed: IndexedObject, values: IndexedObject) => {
    const answer = converDataAfterSave(data, values);
    if (trainingResultState && trainingResultState.step3) {
      const newData = { ...trainingResultState, step3: { ...trainingResultState.step3, answer } };
      setTrainingResultState(newData);
      setConfigTable(configTable1(trainingResultState.training?.template ?? {}, newData));
    }
  };

  const clickChangeRoute = (link: string) => {
    form.submit();
    setUrlLink(link);
  };

  return (
    <>
      <div className={styles.assignmentsQuestionWrap}>
        <HeaderTraining />
        <NavStep type={ENavType.student} onClick={clickChangeRoute} />
        <div className={styles.content}>
          <div className={styles.contentWrap}>
            <div className={styles.contentWrap__left}>
              <div className={styles.contentTitle}>設問</div>
              <div className={styles.form}>
                <Form
                  form={form}
                  onValuesChange={onChangeValue}
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  layout="vertical"
                >
                  {data.map((questions) => questions.questions.map((question) => renderValue(question)))}
                </Form>
              </div>
            </div>
            <div className={styles.contentWrap__right}>
              <div className={styles.contentTitle}>参考情報</div>
              <div className={styles.form}>{configTable && <AssignmentQuestionForm configTable={configTable} />}</div>
            </div>
          </div>
        </div>
      </div>
      <ButtonFooter handelClickNext={onClickNextStep} handelClickPre={onClickPreStep} />
      <ModalConfirm
        open={showModal}
        onClose={() => setShowModal(false)}
        handleConfirm={handelConfirm}
        title="確認"
        subTitle="設問回答がすべて終了していません。ここまでの設問回答を保存して他のページに移動しますか？"
      />
    </>
  );
};

export default AssignmentQuestion3;
